import useGameStore from '../store/gameStore';
import ActionSteps from './ActionSteps';
import DistrictPortfolio from './CapTable';
import PlayerLedgerHud from './PlayerLedgerHud';
import BotStatusBar from './BotStatusBar';
import { AuctionPanel } from './AuctionCards';
import { FastTravelPanel, LobbyPanel } from './StrategyPanels';
import { money } from '../lib/format';
import { useT } from '../i18n';
import { PlayerDot } from './PlayerPawn';
import {
  BankIcon,
  BotIcon,
  ChartIcon,
  CoinsIcon,
  CrownIcon,
  FundIcon,
  LoanIcon,
  ShieldIcon,
  TrophyIcon,
  WalletIcon,
} from './GameIcons';

/**
 * The board's inner "table": the decision card, the player's company dashboard and the
 * live ledger all live inside the playing area. The old right-hand sidebar is gone, so
 * the centre of the board is the control room instead of empty felt.
 */

const StatRow = ({
  icon,
  label,
  value,
  sub,
  tone = 'text-slate-100',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone?: string;
}) => (
  <div className="flex items-center justify-between gap-2 rounded-lg border border-white/5 bg-black/20 px-2 py-1">
    <span className="flex min-w-0 items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">
      <span className="text-slate-500">{icon}</span>
      <span className="truncate">{label}</span>
    </span>
    <span className={`shrink-0 font-mono text-[11px] font-black ${tone}`}>
      {value}
      {sub ? <span className="ml-1 text-[9px] font-bold text-slate-500">{sub}</span> : null}
    </span>
  </div>
);

export const CompanySummary = () => {
  const store = useGameStore();
  const t = useT();
  const { players, currentPlayerIndex, market } = store;
  const active = players[currentPlayerIndex];
  const human = active && !active.isAI ? active : players[0];
  if (!human) return null;

  const portfolio = store.portfolioValueOf(human.id);
  const investments = store.investmentValueOf(human.id);
  const investedPrincipal = store.investments
    .filter((entry) => entry.playerId === human.id)
    .reduce((sum, entry) => sum + entry.principal, 0);
  const loanExposure = store.loanExposureOf(human.id);
  const activeLoans = store.loans.filter((loan) => loan.playerId === human.id).length;
  const ceiling = store.debtCeilingOf(human.id);
  const netWorth = store.netWorthOf(human.id);
  const delta = investments - investedPrincipal;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0b1713]/80 p-2.5 shadow-inner">
      <div className="mb-1.5 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-amber-300/90">
          <WalletIcon size={13} /> {t('board.company')}
        </p>
        <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[9px] font-bold text-slate-300">
          {human.name}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1">
        <StatRow icon={<CoinsIcon size={12} />} label={t('common.cash')} value={money(human.cash)} tone={human.cash < 0 ? 'text-rose-300' : 'text-emerald-200'} />
        <StatRow icon={<BankIcon size={12} />} label={t('common.equityValue')} value={money(portfolio)} />
        <StatRow
          icon={<LoanIcon size={12} />}
          label={t('common.debt')}
          value={money(human.debt)}
          sub={`/ ${money(ceiling)}`}
          tone={human.debt > ceiling ? 'text-rose-300' : 'text-rose-200'}
        />
        <StatRow icon={<LoanIcon size={12} />} label={t('common.loans')} value={money(loanExposure)} sub={activeLoans > 0 ? `${activeLoans}×` : undefined} tone="text-rose-200" />
        <StatRow
          icon={<FundIcon size={12} />}
          label={t('common.investments')}
          value={money(investments)}
          sub={investments > 0 ? `${delta >= 0 ? '+' : ''}${money(delta)}` : undefined}
          tone={delta >= 0 ? 'text-emerald-200' : 'text-rose-200'}
        />
        <StatRow
          icon={<ShieldIcon size={12} />}
          label={t('desk.tabInsurance')}
          value={human.insuranceTurnsRemaining > 0 ? `${human.insuranceTurnsRemaining} ${t('common.rounds')}` : '—'}
          tone={human.insuranceTurnsRemaining > 0 ? 'text-sky-200' : 'text-slate-500'}
        />
        <div className="col-span-2 mt-0.5 flex items-center justify-between rounded-lg border border-amber-300/30 bg-amber-400/10 px-2 py-1">
          <span className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.16em] text-amber-200">
            <TrophyIcon size={12} /> {t('common.netWorth')}
          </span>
          <span className="font-mono text-sm font-black text-amber-100">{money(netWorth)}</span>
        </div>
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2 rounded-lg bg-black/25 px-2 py-1 text-[9px] font-bold text-slate-400">
        <span className="flex items-center gap-1">
          <ChartIcon size={11} /> {market.phase}
        </span>
        <span>{t('common.round')} {market.currentRound}</span>
        <span>{t('board.rentShort')} ×{market.rentMultiplier}</span>
        <span>{t('board.rateShort')} {(market.interestRate * 100).toFixed(0)}%</span>
      </div>
    </div>
  );
};

const BotTurnCard = () => {
  const store = useGameStore();
  const t = useT();
  const active = store.players[store.currentPlayerIndex];
  if (!active?.isAI) return null;
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0b1713]/85 p-3 shadow-inner">
      <p className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">
        <BotIcon size={12} /> {t('board.botTurnNotice')}
      </p>
      <p className="mt-1 flex items-center gap-2 text-lg font-black text-amber-300">
        <span className="h-2.5 w-2.5 animate-pulse rounded-full" style={{ backgroundColor: active.color }} />
        {active.name}
      </p>
      <BotStatusBar />
    </div>
  );
};

const GameOverCard = () => {
  const store = useGameStore();
  const t = useT();
  const standings = [...store.players].sort((a, b) => store.netWorthOf(b.id) - store.netWorthOf(a.id));
  const winner = store.players.find((player) => player.id === store.winnerId);
  return (
    <div className="mt-2 rounded-2xl border border-amber-300/40 bg-amber-400/10 p-2.5">
      <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-amber-200">
        <CrownIcon size={13} /> {t('board.sessionClosed')}
      </p>
      <p className="mt-1 text-xs font-bold text-slate-100">
        {t('board.winner')}: <span className="text-amber-200">{winner?.name ?? '—'}</span>
      </p>
      <div className="mt-1.5 space-y-0.5">
        {standings.map((player) => (
          <div key={player.id} className="flex items-center justify-between gap-2 text-[10px]">
            <span className="flex min-w-0 items-center gap-1.5 font-semibold text-slate-300">
              <PlayerDot player={player} className="h-2 w-2" />
              <span className="truncate">{player.name}</span>
            </span>
            <span className="shrink-0 font-mono font-bold text-amber-200">{money(store.netWorthOf(player.id))}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const BoardTable = () => {
  const store = useGameStore();
  const active = store.players[store.currentPlayerIndex];
  const isBot = Boolean(active?.isAI);
  return (
    <div className="board-table">
      <div className="board-table-left scrollbar-thin">
        {isBot ? <BotTurnCard /> : <ActionSteps />}
        <FastTravelPanel />
        <LobbyPanel />
        <AuctionPanel />
        <CompanySummary />
        <DistrictPortfolio />
      </div>
      <div className="board-table-right scrollbar-thin">
        <PlayerLedgerHud />
        {store.gameOver && <GameOverCard />}
      </div>
    </div>
  );
};

export default BoardTable;
